// OnboardingWizard — first-launch setup flow.
//
// Multi-step rather than one-page so each decision gets a moment of
// breathing room, especially on iPad where smaller text fields feel
// cramped. Steps:
//   0. 歡迎 + 簡介                — read-only, shows what this tool is
//   1. 你叫咩名 + 你嘅 design 角色 — display name + role
//   2. 揀 LLM provider             — radio between minimax / Gemini
//   3. Paste API key + test        — verify by hitting provider once
//   4. 完成                        — celebrate; persist + jump to main
//
// On the last step we POST a tiny throwaway request to the provider with
// the key the user pasted; if that fails (bad key, network), we keep them
// on this step with the error message so they can correct before
// committing settings.

import { useCallback, useMemo, useState } from 'react';

import { createProvider, PROVIDER_REGISTRY, type ProviderSlug } from '../core/providers';
import { useSettings } from '../state/settings';

interface OnboardingWizardProps {
  onComplete: () => void;
}

type Step = 0 | 1 | 2 | 3;

export function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const patch = useSettings((s) => s.patch);
  const [step, setStep] = useState<Step>(0);
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('');
  const [providerSlug, setProviderSlug] = useState<ProviderSlug>('minimax');
  const [apiKey, setApiKey] = useState('');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  const next = useCallback(() => {
    setStep((s) => (s + 1) as Step);
  }, []);
  const back = useCallback(() => {
    setStep((s) => Math.max(0, s - 1) as Step);
  }, []);

  const canAdvance = useMemo(() => {
    if (step === 1) return displayName.trim().length > 0 && role.trim().length > 0;
    if (step === 2) return Boolean(providerSlug);
    if (step === 3) return apiKey.trim().length > 8 && !testing;
    return true;
  }, [step, displayName, role, providerSlug, apiKey, testing]);

  const finish = useCallback(async () => {
    setTestError(null);
    setTesting(true);
    try {
      // Spin up a temporary provider with the user's key and send the
      // smallest possible request. Any 200 response means the key is
      // accepted; we don't care about the body.
      const provider = createProvider({ slug: providerSlug, apiKey: apiKey.trim() });
      let gotAnyEvent = false;
      const ac = new AbortController();
      const timeout = setTimeout(() => ac.abort(), 15000);
      await provider.streamChat({
        messages: [
          { role: 'system', content: '你係一個簡單嘅 echo 機器人。' },
          { role: 'user', content: 'ping' },
        ],
        tools: [],
        signal: ac.signal,
        onEvent: (e) => {
          if (e.type === 'text_delta' || e.type === 'message_complete') {
            gotAnyEvent = true;
          }
        },
      });
      clearTimeout(timeout);
      if (!gotAnyEvent) {
        throw new Error('Provider 冇返任何 event — key 或者 network 有問題');
      }
      await patch({
        setupComplete: true,
        displayName: displayName.trim(),
        role: role.trim(),
        providerSlug,
        apiKey: apiKey.trim(),
      });
      onComplete();
    } catch (err) {
      setTestError(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  }, [providerSlug, apiKey, displayName, role, patch, onComplete]);

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-10">
      <div className="w-full max-w-[560px] rounded-xl border border-[#222230] bg-[#0d0d12] p-8 shadow-2xl">
        <header className="mb-6 flex items-baseline justify-between">
          <h1 className="miniken-wordmark text-[20px] leading-none">
            MiniKen<span className="period">.</span>
            <span className="ml-2 align-middle text-[12px] font-medium tracking-[0.04em] text-[#9090A8]">
              Design Studio · Open
            </span>
          </h1>
          <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-600">
            Step {step + 1} / 4
          </span>
        </header>

        {step === 0 && <StepWelcome />}
        {step === 1 && (
          <StepIdentity
            displayName={displayName}
            setDisplayName={setDisplayName}
            role={role}
            setRole={setRole}
          />
        )}
        {step === 2 && (
          <StepProvider providerSlug={providerSlug} setProviderSlug={setProviderSlug} />
        )}
        {step === 3 && (
          <StepApiKey
            providerSlug={providerSlug}
            apiKey={apiKey}
            setApiKey={setApiKey}
            testing={testing}
            testError={testError}
          />
        )}

        <div className="mt-8 flex items-center justify-between border-t border-[#222230] pt-5">
          <button
            type="button"
            onClick={back}
            disabled={step === 0 || testing}
            className="text-[12px] text-neutral-500 hover:text-neutral-300 disabled:cursor-not-allowed disabled:opacity-30"
          >
            ← 返回
          </button>
          {step < 3 ? (
            <button
              type="button"
              onClick={next}
              disabled={!canAdvance}
              className="rounded-md border border-[#8AFF45] bg-[#8AFF45]/15 px-5 py-2 text-[12.5px] font-semibold text-[#8AFF45] hover:bg-[#8AFF45]/25 disabled:cursor-not-allowed disabled:opacity-30"
            >
              下一步 →
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void finish()}
              disabled={!canAdvance || testing}
              className="rounded-md border border-[#8AFF45] bg-[#8AFF45]/15 px-5 py-2 text-[12.5px] font-semibold text-[#8AFF45] hover:bg-[#8AFF45]/25 disabled:cursor-not-allowed disabled:opacity-30"
            >
              {testing ? '測試 key 中…' : '✓ 完成設定'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Steps ---------------------------------------------------------------

function StepWelcome() {
  return (
    <div className="space-y-4 text-[13px] leading-relaxed text-neutral-300">
      <h2 className="text-[18px] font-semibold tracking-tight text-[#F0F0F5]">
        歡迎使用 Open 版
      </h2>
      <p>
        呢套工具係 <span className="text-[#8AFF45]">MiniKen Design Studio</span> 嘅 team-edition 旁支。
        所有檔案都儲存喺你呢部 device 嘅本地 storage（OPFS），<strong className="text-neutral-100">唔會 sync 上雲</strong>，
        每個 project 都係 one-of-one。
      </p>
      <p>
        你會用自己嘅 LLM API key 驅動 agent —— 我哋唔代為認證、唔睇你個 key、唔記錄你嘅對話。
        cost 自負，你 control 喺度做幾多事。
      </p>
      <p className="text-[12px] text-neutral-500">
        準備好嘅話，撳「下一步」開始 setup。整個過程 1 分鐘搞掂。
      </p>
    </div>
  );
}

function StepIdentity({
  displayName,
  setDisplayName,
  role,
  setRole,
}: {
  displayName: string;
  setDisplayName: (s: string) => void;
  role: string;
  setRole: (s: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[18px] font-semibold tracking-tight text-[#F0F0F5]">
          認識你
        </h2>
        <p className="mt-1 text-[12px] leading-relaxed text-neutral-500">
          Agent 會根據呢啲資料調整 tone 同 reference。永遠唔會傳上雲。
        </p>
      </div>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">
          你叫咩
        </span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          autoFocus
          placeholder="e.g. Sarah Wong"
          className="mt-1.5 w-full rounded-md border border-[#222230] bg-[#050508] px-3 py-2 text-[13px] text-[#F0F0F5] outline-none focus:border-[#8AFF45]"
        />
      </label>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">
          你嘅角色 / design 背景
        </span>
        <textarea
          value={role}
          onChange={(e) => setRole(e.target.value)}
          rows={3}
          placeholder="e.g. 保險顧問，主要做退休 / 醫療類 deck，bilingual zh-HK 客戶"
          className="mt-1.5 w-full resize-y rounded-md border border-[#222230] bg-[#050508] px-3 py-2 text-[13px] text-[#F0F0F5] outline-none focus:border-[#8AFF45]"
        />
        <span className="mt-1 block text-[10.5px] text-neutral-600">
          一兩句搞掂。Agent 會用呢段做 sharper discovery questions 嘅起點。
        </span>
      </label>
    </div>
  );
}

function StepProvider({
  providerSlug,
  setProviderSlug,
}: {
  providerSlug: ProviderSlug;
  setProviderSlug: (s: ProviderSlug) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[18px] font-semibold tracking-tight text-[#F0F0F5]">
          揀 LLM provider
        </h2>
        <p className="mt-1 text-[12px] leading-relaxed text-neutral-500">
          隨時可以喺 Settings 改。每個 provider 嘅定價同強項唔同。
        </p>
      </div>
      <div className="space-y-3">
        {PROVIDER_REGISTRY.map((p) => {
          const selected = p.slug === providerSlug;
          return (
            <button
              key={p.slug}
              type="button"
              onClick={() => setProviderSlug(p.slug)}
              className={`flex w-full items-start gap-3 rounded-md border px-4 py-3 text-left transition-colors ${
                selected
                  ? 'border-[#8AFF45] bg-[#8AFF45]/10'
                  : 'border-[#222230] bg-[#050508] hover:border-[#333348]'
              }`}
            >
              <span
                className={`mt-1 inline-block h-3 w-3 rounded-full border ${
                  selected ? 'border-[#8AFF45] bg-[#8AFF45]' : 'border-[#444454]'
                }`}
                aria-hidden
              />
              <span className="flex-1">
                <span className="block text-[13.5px] font-semibold text-[#F0F0F5]">
                  {p.label}
                </span>
                <span className="mt-0.5 block text-[11px] text-neutral-500">
                  default model · {p.defaultModel}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepApiKey({
  providerSlug,
  apiKey,
  setApiKey,
  testing,
  testError,
}: {
  providerSlug: ProviderSlug;
  apiKey: string;
  setApiKey: (s: string) => void;
  testing: boolean;
  testError: string | null;
}) {
  const meta = PROVIDER_REGISTRY.find((p) => p.slug === providerSlug)!;
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[18px] font-semibold tracking-tight text-[#F0F0F5]">
          貼入 {meta.label} API key
        </h2>
        <p className="mt-1 text-[12px] leading-relaxed text-neutral-500">
          {meta.apiKeyHelpText}
          <br />
          <a
            href={meta.apiKeyHelpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#8AFF45] underline-offset-2 hover:underline"
          >
            {meta.apiKeyHelpUrl}
          </a>
        </p>
      </div>
      <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.14em] text-neutral-500">
          API key
        </span>
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          autoFocus
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={`貼喺呢度…（${meta.label} key 通常以特定 prefix 開頭）`}
          className="mt-1.5 w-full rounded-md border border-[#222230] bg-[#050508] px-3 py-2 font-mono text-[12px] text-[#F0F0F5] outline-none focus:border-[#8AFF45]"
        />
      </label>
      <p className="text-[10.5px] leading-relaxed text-neutral-600">
        撳「完成設定」會用呢個 key 試發一個極小 request 確認佢有效。Key 完全儲喺你部 device 嘅 IndexedDB，唔會傳俾我哋 — 即使呢個 web app 都唔可以連 cloud。
      </p>
      {testError && (
        <p className="rounded-md border border-red-900/60 bg-red-950/40 px-3 py-2 text-[11.5px] leading-relaxed text-red-300">
          ⚠ Key 試唔通：{testError}
        </p>
      )}
      {testing && (
        <p className="text-[11px] text-neutral-500">
          測試緊個 key…（最多 15 秒 timeout）
        </p>
      )}
    </div>
  );
}
